import React, {Fragment, useState, useEffect} from 'react';
import styled from 'styled-components';
import {Row, Col} from 'antd';
import {Expander, Text} from '../Widgets';
import {Link} from 'gatsby';
import {Add, ExpandLess} from '@styled-icons/material';
import SocialLinks from './SocialLinks';
import Image from './Image';
import {COPYRIGHT_SEPARATOR} from '../Components/CopyRightLinks';
const config = require('../data.json');

const {footer} = config;

function withPhoneClick(tel) {
  try {
    let _tel = tel.split('-').join('');
    if (_tel[0] !== '+' && _tel[0] !== '0' && _tel[0] !== '8') {
      throw 'wrong format';
    }
    return (
      <a
        aria-label={'tel：' + _tel}
        href={`tel:${_tel}`}
        style={{color: 'black', textDecoration: 'none'}}>
        {tel}
      </a>
    );
  } catch (e) {
    return tel;
  }
}

function withMailClick(mail) {
  return (
    <a
      aria-label={'mailto：' + mail}
      href={`mailto:${mail}`}
      style={{color: 'black', textDecoration: 'none'}}>
      {mail}
    </a>
  );
}

// 版權年份顯示為「起始年~今年」，未設定 copyrightStartYear 的平台維持只顯示 copyright
function getCopyrightLabel() {
  const {copyright, copyrightStartYear} = config.information;

  if (!copyrightStartYear) {
    return copyright;
  }

  return `${copyrightStartYear}~${new Date().getFullYear()} ${copyright}`;
}

function withUrlClick(text, url) {
  if (url === '' || !url) {
    return text;
  } else {
    return (
      <a
        aria-label={text + '-' + url}
        href={url}
        target="_blank"
        style={{color: 'black', textDecoration: 'none'}}
        rel="noreferrer">
        {text}
      </a>
    );
  }
}

const Label = ({children, size = 'xs', color}) => (
  <Text size={size} color={color} style={{marginBottom: 12}} weight="500">
    {children}
  </Text>
);
const Value = ({children}) => (
  <Text
    size="sm"
    weight="400"
    color={footer.textColor}
    style={{marginBottom: 12, whiteSpace: 'break-spaces'}}>
    {children}
  </Text>
);

function Block({block}) {
  return (
    <Fragment>
      <Label color={footer.labelColor} size="sm">
        {block.label}
      </Label>
      {block.items.map((item, idx) => (
        <Value key={idx}>
          {item.url ? (
            <a
              href={item.url}
              rel="noopener noreferrer"
              style={{color: '#40444C'}}>
              {item.text}
            </a>
          ) : (
            item.text
          )}
        </Value>
      ))}
    </Fragment>
  );
}

export default function Footer(props) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return (
    <Wrapper>
      <Content>
        <Row gutter={[16, 16]} style={{marginBottom: 48}}>
          <Col md={3} sm={8} xs={24}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Image
                src="/images/logo-word.png"
                alt="logo"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: 106,
                  marginBottom: 10,
                }}
                width="106"
                height="45"
              />
            </div>
          </Col>
          <Col md={10} sm={16} xs={24}>
            <Block block={footer.blocks[0]} />
          </Col>
          <Col md={2} sm={4} xs={0}>
            <Block block={footer.blocks[1]} />
          </Col>
          <Col md={3} sm={4} xs={0}>
            <Block block={footer.blocks[2]} />
          </Col>
          <Col md={6} sm={16} xs={0}>
            <Block block={footer.blocks[3]} />
          </Col>
          <Col md={0} sm={0} xs={24}>
            {footer.blocks
              .filter((_, index) => index > 0)
              .map((block, idx) => (
                <Expander
                  key={idx}
                  title={block.label}
                  ExpandMoreIcon={() => <Add size={12} color="#5a5a5a" />}
                  ExpandLessIcon={() => (
                    <ExpandLess size={12} color="#5a5a5a" />
                  )}
                  titleStyle={{fontSize: 16}}
                  contentStyle={{padding: '10px 20px'}}>
                  {block.items.map((item, idx) => (
                    <Value key={idx}>{item.text}</Value>
                  ))}
                </Expander>
              ))}
          </Col>
        </Row>
        <SocialLinks />
      </Content>

      <CopyrightSection />
    </Wrapper>
  );
}

const CopyrightSection = () => {
  const {width} = useWindowDimensions();

  if (width > config.breakpoints.sm) {
    return (
      <Copyright css="height:42px;display:flex;justify-content:center;align-items:center;">
        {/* xixi 客製：桌機版 sub-footer 字級放大為 14px，且不顯示隱私政策／服務聲明連結 */}
        <span style={{fontSize: 14}}>
          {getCopyrightLabel()}
          {COPYRIGHT_SEPARATOR}
          Powered By&nbsp;
          <a href="https://eculture.tech/" target="_blank" rel="noreferrer">
            奕果雲端數位 EcultureTech
          </a>
        </span>
      </Copyright>
    );
  }

  return (
    <Copyright>
      {/* xixi 客製：手機版版權 14px、Powered By 10px、公司連結 12px */}
      <div style={{fontSize: 14}}>{getCopyrightLabel()}</div>
      <div style={{fontSize: 14}}>
        <span style={{fontSize: 10}}>Powered By&nbsp;</span>
        <a
          href="https://eculture.tech/"
          target="_blank"
          rel="noreferrer"
          style={{fontSize: 12}}>
          奕果雲端數位 EcultureTech
        </a>
      </div>
    </Copyright>
  );
};

function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = React.useState(
    getWindowDimensions(),
  );

  function getWindowDimensions() {
    const {innerWidth: width, innerHeight: height} =
      typeof window !== 'undefined'
        ? window
        : {
            innerWidth: 1024,
            innerHeight: 800,
          };
    return {
      width,
      height,
    };
  }

  React.useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  return windowDimensions;
}

const Wrapper = styled.div`
  background-color: ${config.footer.backgroundColor};
  min-height: 250px;
  display: flex;
  flex-direction: column;
  box-shadow: ${config.footer.boxShadow ? '0px -2px 8px -4px #555' : 'none'};
  z-index: 1;
`;

const Content = styled.div`
  max-width: ${config.maxContentWidth}px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 0 20px 0;
  flex: 1;
  color: ${config.colors.textSecond};
  font-size: 12px;

  & > p {
  }

  & .nav-item {
    margin-bottom: 12px;
    color: ${config.colors.textSecond};
    text-decoration: none;
    display: flex;
    align-items: center;
  }

  & > .title {
    font-weight: 500;
  }

  @media screen and (max-width: ${config.maxContentWidth}px) {
    padding: 40px 20px 20px 20px;
  }

  @media screen and (max-width: ${config.breakpoints.sm}px) {
    padding: 40px 20px 20px 20px;
  }

  ${(props) => props.css || ''};
`;

const Copyright = styled.div`
  padding: 15px;
  background-color: ${config.footer.copyrightBackgroundColor ||
  config.colors.main};
  text-align: center;
  align-self: stretch;
  /* sub-footer 內所有文字與連結共用同一組字級、字重與顏色 */
  color: ${config.footer.copyrightColor || config.colors.text};
  font-size: ${config.fontSize.xs}px;
  font-weight: 400;

  & a {
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    text-decoration: none;
  }
`;
